import * as React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useRouteMatch } from 'react-router-dom';
import { server as StardogServer } from 'stardog';
import { BaseConnection, getStardogConnection } from 'vet-bones/utils';

import {
  DEMO_CONNECTION_INDEX,
  DEMO_KIT_DB_DEFAULT,
} from 'src/ui/features/connection';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  Connection as GraphConnection,
  useGetConnectionByIndexQuery,
  useMarketplaceSettingsQuery,
} from 'src/ui/graph/types';
import { useProfile } from 'src/ui/hooks/connection';
import {
  marketplaceSettingsToDbOptions,
  useModuleMeta,
} from 'src/ui/hooks/modules';
import { previousConnectionData } from 'src/ui/hooks/previousConnectionData';
import { runWithRefetchConnectionToken } from 'src/ui/hooks/refetchConnectionToken';
import {
  useConnectionServerStatus,
  voiceboxLimitFromStatus,
} from 'src/ui/hooks/serverStatus';

export const DEFAULT_CATALOG_DATABASE_ID = 'catalog';
export const DEFAULT_VOICEBOX_NAMED_GRAPH = 'tag:stardog:api:context:local';

type VoiceboxPageConnectionData = {
  catalogDatabaseId: string;
  connectionIndex: string;
  conversationId?: string;
};

type VoiceboxDrawerConnectionData = {
  catalogDatabaseId: string;
  connectionIndex: string;
  databaseId: string;
  model: string;
  namedGraphs: string[];
};

/**
 * Whether the server has a Voicebox license seat limit.
 *
 * Derived from the shared `/admin/status` query rather than its own request —
 * see `useConnectionServerStatus`. `data` is `null` until the connection is
 * usable, `undefined` while the first status request is in flight.
 */
export const useIsVoiceboxEnabledServer = (
  connectionIndex: string,
  orgDomain: string
) => {
  const { data: connectionData } = useGetConnectionByIndexQuery(
    graphQLClient,
    { index: Number(connectionIndex), org_domain: orgDomain },
    { enabled: Boolean(connectionIndex) }
  );
  const connection = connectionData?.connection as GraphConnection | null;

  const { data: status, isLoading } = useConnectionServerStatus(
    connection,
    orgDomain
  );

  return React.useMemo(() => {
    // `undefined` status means the first request is still in flight — that is
    // not the same as "no Voicebox seats", so don't answer false yet. A `null`
    // status (the request failed) does mean not enabled, matching the previous
    // behavior of this hook.
    const data =
      !connection || status === undefined
        ? null
        : Boolean(voiceboxLimitFromStatus(status));
    return { data, isLoading };
  }, [connection, isLoading, status]);
};

const useCatalogDatabaseId = (connectionIndex: string, orgDomain: string) => {
  const queryClient = useQueryClient();
  const { data: connection } = useGetConnectionByIndexQuery(
    graphQLClient,
    { index: Number(connectionIndex), org_domain: orgDomain },
    { enabled: Boolean(connectionIndex) }
  );

  const stardogConnection = connection?.connection
    ? getStardogConnection(connection.connection as BaseConnection, {
        agent: 'Portal',
      })
    : null;

  const keyPrefix = [
    'useCatalogDatabaseId',
    stardogConnection ? connectionIndex : null,
  ];

  return useQuery<string | null>(
    [...keyPrefix, connection?.connection?.token ?? null],
    async () => {
      if (!stardogConnection) {
        return null;
      }

      if (connectionIndex === DEMO_CONNECTION_INDEX.toString()) {
        return DEFAULT_CATALOG_DATABASE_ID;
      }

      try {
        const response = await runWithRefetchConnectionToken(
          orgDomain
        )(stardogConnection, Number(connectionIndex), (stardogConn) =>
          StardogServer.properties(stardogConn, ['catalog.database'])
        );
        if (!response.ok) {
          console.error(
            `problem requesting useCatalogDatabaseId: ${
              response.statusText || String(response.status)
            }`
          );
          return DEFAULT_CATALOG_DATABASE_ID;
        }

        return (
          response.body?.['catalog.database'] || DEFAULT_CATALOG_DATABASE_ID
        );
      } catch (err) {
        console.error(`problem requesting useCatalogDatabaseId: ${err}`);
        return DEFAULT_CATALOG_DATABASE_ID;
      }
    },
    {
      placeholderData: () =>
        previousConnectionData<string | null>(queryClient, keyPrefix),
    }
  );
};

const useVoiceboxPageData = (
  connectionIndex: string,
  orgDomain: string,
  conversationId?: string
): VoiceboxPageConnectionData | null => {
  const maybeCatalogDatabaseId = useCatalogDatabaseId(
    connectionIndex,
    orgDomain
  );
  const catalogDatabaseId =
    maybeCatalogDatabaseId.data || DEFAULT_CATALOG_DATABASE_ID;

  return React.useMemo(
    () => ({ catalogDatabaseId, connectionIndex, conversationId }),
    [catalogDatabaseId, connectionIndex, conversationId]
  );
};

export const useVoiceboxPageConnectionData = (): VoiceboxPageConnectionData | null => {
  const pageMatch = useRouteMatch<{
    id: string;
    org?: string;
    conversation?: string;
  }>({
    exact: true,
    path: [
      '/u/:id(\\d+)/voicebox/:conversation?',
      '/o/:org([a-z0-9-]+)/:id(\\d+)/voicebox/:conversation?',
    ],
  });
  const isVoiceboxPage = Boolean(pageMatch);

  const connectionIndex = pageMatch?.params.id || '';
  const { data: isVoiceboxEnabledServer } = useIsVoiceboxEnabledServer(
    connectionIndex,
    pageMatch?.params.org || ''
  );

  const { data: profileData } = useProfile();
  const isVoiceboxEnabledUser = profileData?.profile?.is_voicebox_enabled;

  const pageConnectionData = useVoiceboxPageData(
    connectionIndex,
    pageMatch?.params.org || '',
    pageMatch?.params.conversation
  );

  return React.useMemo(() => {
    if (isVoiceboxPage && isVoiceboxEnabledUser && isVoiceboxEnabledServer) {
      return pageConnectionData;
    }

    return null;
  }, [
    isVoiceboxEnabledServer,
    isVoiceboxEnabledUser,
    isVoiceboxPage,
    pageConnectionData,
  ]);
};

const useVoiceboxDrawerData = (
  id: string,
  enabled = true
): VoiceboxDrawerConnectionData | null => {
  const { data: marketplaceSettingsData } = useMarketplaceSettingsQuery(
    graphQLClient
  );
  const marketplaceConfig = marketplaceSettingsToDbOptions(
    marketplaceSettingsData?.marketplaceSettings
  );
  const { data: moduleData } = useModuleMeta(
    id || '',
    enabled ? marketplaceConfig : null
  );

  const connectionIndex = DEMO_CONNECTION_INDEX.toString();

  const catalogDatabaseId = DEFAULT_CATALOG_DATABASE_ID;

  const isVoiceboxKitEnabled = moduleData?.moduleMeta?.voiceboxEnabled;

  const databaseId =
    moduleData?.module?.databaseId ||
    marketplaceConfig?.database ||
    DEMO_KIT_DB_DEFAULT;

  const model = moduleData?.module?.schemaName || '';

  const namedGraph = moduleData?.module?.alias || DEFAULT_VOICEBOX_NAMED_GRAPH;

  const data = React.useMemo(
    () =>
      isVoiceboxKitEnabled
        ? {
            catalogDatabaseId,
            connectionIndex,
            databaseId,
            model,
            namedGraphs: [namedGraph],
          }
        : null,
    [
      catalogDatabaseId,
      connectionIndex,
      databaseId,
      isVoiceboxKitEnabled,
      model,
      namedGraph,
    ]
  );

  return data;
};

export const useVoiceboxDrawerConnectionData = (): VoiceboxDrawerConnectionData | null => {
  const { data: profileData } = useProfile();
  const isVoiceboxEnabledUser = profileData?.profile?.is_voicebox_enabled;

  const kitsMatch = useRouteMatch<{ id: string }>({
    exact: true,
    path: ['/kits/:id([\\w:\\d\\-\\.%]+)'],
  });
  const isKitsPage = Boolean(kitsMatch);
  const kitsConnectionData = useVoiceboxDrawerData(
    kitsMatch?.params.id || '',
    isKitsPage
  );

  return React.useMemo(() => {
    if (isKitsPage && isVoiceboxEnabledUser) {
      return kitsConnectionData;
    }

    return null;
  }, [isKitsPage, isVoiceboxEnabledUser, kitsConnectionData]);
};

/** returns true if we know for sure the user shouldn't access the VoiceboxPage */
export const useNotAllowedVoiceboxPage = (
  connectionIndex: string,
  orgDomain: string
): { loading: boolean; notAllowed: boolean } => {
  const { data: isEnabled, isLoading } = useIsVoiceboxEnabledServer(
    connectionIndex,
    orgDomain
  );

  return React.useMemo(() => {
    // `undefined` before the first fetch resolves, `null` when the connection
    // isn't usable yet — neither is an answer about this connection.
    if (isLoading || typeof isEnabled !== 'boolean') {
      return {
        loading: true,
        notAllowed: false,
      };
    }

    return {
      loading: false,
      notAllowed: !isEnabled,
    };
  }, [isEnabled, isLoading]);
};
